// ═══════════════════════════════════════════════════════════════
// CULTIVATION OVERVIEW TAB — Stats, funnel, quick wins
// Path: src/components/cultivation/CultivationOverviewTab.jsx
// ═══════════════════════════════════════════════════════════════

import React from "react";
import { TrendingUp, Star, ChevronRight } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, ICON_SIZES } from "../../constants/appConfig";
import { Card } from "../ui/Card";
import { EngagementMeter, StatBubble } from "../../utils/cultivationUtils";

export const CultivationOverviewTab = ({
  enriched,
  segments,
  onMemberClick,
}) => {
  const { palette, members } = useApp();

  const avgScore =
    members.length > 0
      ? Math.round(
          enriched.reduce((s, m) => s + m.engagement.score, 0) / members.length
        )
      : 0;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", width: "100%" }}>
      {/* Stat Bubbles */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <Card style={{ padding: 20, textAlign: "center" }}>
          <StatBubble
            value={avgScore}
            label="Avg Engagement"
            color={avgScore >= 50 ? "#10b981" : "#f59e0b"}
            palette={palette}
          />
        </Card>
        <Card style={{ padding: 20, textAlign: "center" }}>
          <StatBubble
            value={segments.prospects.length}
            label="Prospects"
            color="#ea580c"
            palette={palette}
          />
        </Card>
        <Card style={{ padding: 20, textAlign: "center" }}>
          <StatBubble
            value={segments.donors.length}
            label="Current Donors"
            color="#10b981"
            palette={palette}
          />
        </Card>
        <Card style={{ padding: 20, textAlign: "center" }}>
          <StatBubble
            value={segments.readyForAsk.length}
            label="Ready for Ask"
            color="#8b5cf6"
            palette={palette}
          />
        </Card>
        <Card style={{ padding: 20, textAlign: "center" }}>
          <StatBubble
            value={segments.noContact.length}
            label="No Contact Yet"
            color={segments.noContact.length > 10 ? "#ef4444" : "#f59e0b"}
            palette={palette}
          />
        </Card>
        <Card style={{ padding: 20, textAlign: "center" }}>
          <StatBubble
            value={segments.needsAttention.length}
            label="Donors at Risk"
            color="#ef4444"
            palette={palette}
          />
        </Card>
      </div>

      {/* Engagement Funnel */}
      <Card style={{ marginBottom: 16 }}>
        <h3
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            margin: "0 0 16px",
            fontSize: FONT_SIZES.md,
            fontWeight: 600,
            color: palette.text1,
            paddingBottom: 12,
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          <TrendingUp size={ICON_SIZES.md} style={{ color: palette.accent1 }} /> Engagement
          Funnel
        </h3>
        {[
          {
            label: "Lead",
            desc: "80+ pts — Champions, steward and celebrate",
            count: segments.invested.length,
            color: "#10b981",
            width: 100,
          },
          {
            label: "Contribute",
            desc: "30–79 pts — Giving time or money",
            count: segments.engaged.length,
            color: "#3b82f6",
            width: 80,
          },
          {
            label: "Connect",
            desc: "10–29 pts — Building relationship",
            count: segments.warming.length,
            color: "#f59e0b",
            width: 60,
          },
          {
            label: "Cold",
            desc: "0–9 pts — Not yet engaged",
            count: segments.cold.length,
            color: "#ef4444",
            width: 40,
          },
        ].map((tier) => (
          <div key={tier.label} style={{ marginBottom: 14 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 6,
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: FONT_SIZES.sm,
                    fontWeight: 600,
                    color: tier.color,
                  }}
                >
                  {tier.label}
                </span>
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: palette.text3,
                    marginLeft: 8,
                  }}
                >
                  {tier.desc}
                </span>
              </div>
              <span
                style={{
                  fontSize: FONT_SIZES.md,
                  fontWeight: 700,
                  color: tier.color,
                }}
              >
                {tier.count}
              </span>
            </div>
            <div
              style={{
                height: 24,
                borderRadius: 6,
                background: `${palette.accent1}06`,
                overflow: "hidden",
                width: `${tier.width}%`,
                margin: "0 auto",
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: 6,
                  background: tier.color,
                  width: members.length
                    ? `${(tier.count / members.length) * 100}%`
                    : "0%",
                  minWidth: tier.count > 0 ? 24 : 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "width 0.4s ease",
                }}
              >
                {tier.count > 0 && (
                  <span
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontWeight: 700,
                      color: "#fff",
                    }}
                  >
                    {Math.round((tier.count / members.length) * 100)}%
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </Card>

      {/* Quick Wins — Ready for Ask */}
      {segments.readyForAsk.length > 0 && (
        <Card>
          <h3
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              margin: "0 0 16px",
              fontSize: FONT_SIZES.md,
              fontWeight: 600,
              color: palette.text1,
              paddingBottom: 12,
              borderBottom: `1px solid ${palette.cardBorder}`,
            }}
          >
            <Star size={ICON_SIZES.md} style={{ color: "#f59e0b" }} /> Ready for a Giving
            Conversation
          </h3>
          <p
            style={{
              fontSize: FONT_SIZES.sm,
              color: palette.text2,
              margin: "0 0 16px",
            }}
          >
            These non-donors have strong engagement scores. A personal
            conversation about giving could resonate.
          </p>
          {segments.readyForAsk.slice(0, 8).map((m) => (
            <div
              key={m.id}
              onClick={() => onMemberClick(m)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 14px",
                borderRadius: 8,
                cursor: "pointer",
                marginBottom: 4,
                border: `1px solid ${palette.cardBorder}`,
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${palette.accent1}06`;
                e.currentTarget.style.borderColor = palette.accent1;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = palette.cardBorder;
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: FONT_SIZES.sm,
                    fontWeight: 600,
                    color: palette.text1,
                  }}
                >
                  {m.firstName} {m.lastName}
                </span>
                {m.organization && (
                  <span
                    style={{
                      fontSize: FONT_SIZES.xs,
                      color: palette.text3,
                      marginLeft: 8,
                    }}
                  >
                    {m.organization}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <EngagementMeter score={m.engagement.score} palette={palette} />
                <ChevronRight size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
};
